import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { orgAdmin } from "../../api";
import { PageHeader, Btn, LoadingSpinner, StatCard, Badge, FormField, inputStyle, selectStyle } from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

const TABS = ["Overview", "Staff", "Settings"];

export default function BranchManagePage() {
  const { branchId } = useParams();
  const navigate     = useNavigate();
  const [branch,   setBranch]   = useState(null);
  const [settings, setSettings] = useState(null);
  const [staff,    setStaff]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState(0);
  const [saving,   setSaving]   = useState(false);
  const [dirty,    setDirty]    = useState(false);
  const [inviteModal, setInviteModal] = useState(false);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const [br, st, sf] = await Promise.all([
        orgAdmin.getBranch(branchId),
        orgAdmin.getBranchSettings(branchId).catch(() => ({ data: null })),
        orgAdmin.getBranchStaff({ facility: branchId }).catch(() => ({ data: [] })),
      ]);
      setBranch(br.data);
      setSettings(st.data);
      setStaff(sf.data?.results || sf.data || []);
    } catch { toast.error("Failed to load branch."); }
    finally  { setLoading(false); }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setSettings(p => ({ ...p, [k]: v })); setDirty(true); };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await orgAdmin.updateBranchSettings(settings);
      toast.success("Settings saved.");
      setDirty(false);
    } catch { toast.error("Failed to save settings."); }
    finally  { setSaving(false); }
  };

  if (loading) return <div style={{ padding:"32px" }}><LoadingSpinner /></div>;
  if (!branch) return <div style={{ padding:"32px", color:"#EF4444" }}>Branch not found.</div>;

  const statusColor = branch.status === "active" ? "#22C55E" : branch.status === "trial" ? "#EAB308" : "#EF4444";

  return (
    <div style={styles.root}>
      {/* Breadcrumb */}
      <div style={styles.breadcrumb}>
        <button onClick={() => navigate("/org/branches")} style={styles.breadcrumbLink}>Branches</button>
        <span style={{ color:"var(--text-faint)" }}> › </span>
        <span style={{ color:"var(--text-secondary)" }}>{branch.name}</span>
      </div>

      <PageHeader
        title={branch.name}
        subtitle={`${branch.address || "No address set"} · ${branch.slug}`}
        action={
          <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
            <span style={{ fontSize:"12px", fontWeight:"600", padding:"4px 12px", borderRadius:"20px", background: `${statusColor}18`, color: statusColor, border:`1px solid ${statusColor}30` }}>
              {branch.status}
            </span>
            {dirty && <Btn onClick={handleSaveSettings} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</Btn>}
          </div>
        }
      />

      <div style={styles.tabs}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{ ...styles.tab, ...(tab === i ? styles.tabActive : {}) }}>{t}</button>
        ))}
      </div>

      <div style={styles.panel}>
        {tab === 0 && <OverviewTab branch={branch} staff={staff} />}
        {tab === 1 && <StaffTab staff={staff} branchId={branchId} onRefresh={load} onInvite={() => setInviteModal(true)} />}
        {tab === 2 && settings && <SettingsTab s={settings} set={set} />}
        {tab === 2 && !settings && <div style={{ color:"var(--text-secondary)", fontSize:"14px" }}>Settings unavailable for this branch.</div>}
      </div>

      <InviteStaffModal open={inviteModal} onClose={() => setInviteModal(false)} branchId={branchId} onDone={() => { setInviteModal(false); load(); }} />
    </div>
  );
}

function OverviewTab({ branch, staff }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
      <div style={{ display:"flex", gap:"12px", flexWrap:"wrap" }}>
        <StatCard label="Total Staff"   value={staff.length}                            color="#8CBCE0" />
        <StatCard label="Active Staff"  value={staff.filter(s => s.is_active).length}  color="#22C55E" />
        <StatCard label="Tier"          value={branch.tier}                             color="#A78BFA" />
      </div>
      <div style={styles.infoCard}>
        <div style={styles.infoCardTitle}>Branch Details</div>
        <div style={styles.infoCardBody}>
          {[
            ["Name",      branch.name],
            ["Slug",      branch.slug],
            ["Email",     branch.email || "—"],
            ["Phone",     branch.phone || "—"],
            ["Address",   branch.address || "—"],
            ["Tier",      branch.tier],
            ["Status",    branch.status],
          ].map(([k, v]) => (
            <div key={k} style={styles.infoRow}>
              <span style={styles.infoKey}>{k}</span>
              <span style={styles.infoVal}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StaffTab({ staff, branchId, onRefresh, onInvite }) {
  const handleDeactivate = async (user) => {
    try {
      // Use the orgAdmin reset endpoint — deactivation via staff endpoint
      await orgAdmin.resetBranchUserPw(user.id, { deactivate: true });
      toast.success(`${user.name} deactivated.`);
      onRefresh();
    } catch { toast.error("Failed to deactivate."); }
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
      <div style={{ display:"flex", justifyContent:"flex-end" }}>
        <Btn size="sm" onClick={onInvite}>+ Invite Staff</Btn>
      </div>
      {staff.length === 0 ? (
        <div style={{ color:"var(--text-muted)", fontSize:"14px", padding:"24px", textAlign:"center" }}>No staff in this branch yet.</div>
      ) : staff.map(u => (
        <div key={u.id} style={styles.staffRow}>
          <div style={styles.staffAvatar}>{u.name?.[0]?.toUpperCase() || "?"}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:"14px", fontWeight:"600", color:"var(--text-primary)" }}>{u.name}</div>
            <div style={{ fontSize:"12px", color:"var(--text-secondary)" }}>{u.email}</div>
            {u.station_name && <div style={{ fontSize:"11px", color:"var(--text-muted)" }}>Station: {u.station_name}</div>}
          </div>
          <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
            <span style={{ fontSize:"11px", padding:"2px 8px", borderRadius:"10px", background: u.is_active ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", color: u.is_active ? "#22C55E" : "#EF4444", border:`1px solid ${u.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}` }}>
              {u.role}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsTab({ s, set }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>
      <div style={styles.infoCard}>
        <div style={styles.infoCardTitle}>Queue Behaviour</div>
        <div style={styles.infoCardBody}>
          <FormField label="Issuance Mode">
            <select style={selectStyle} value={s.issuance_mode || "kiosk_and_staff"} onChange={e => set("issuance_mode", e.target.value)}>
              <option value="kiosk_only">Kiosk Only</option>
              <option value="kiosk_and_staff">Kiosk + Staff</option>
              <option value="staff_only">Staff Only</option>
            </select>
          </FormField>
        </div>
      </div>
      <div style={styles.infoCard}>
        <div style={styles.infoCardTitle}>Kiosk</div>
        <div style={styles.infoCardBody}>
          <FormField label="Welcome Message (English)">
            <input style={inputStyle} value={s.kiosk_welcome_en || ""} onChange={e => set("kiosk_welcome_en", e.target.value)} placeholder="Welcome. Please get your queue number." />
          </FormField>
          <FormField label="Theme">
            <select style={selectStyle} value={s.kiosk_theme || "dark_blue"} onChange={e => set("kiosk_theme", e.target.value)}>
              <option value="dark_blue">Dark Blue</option>
              <option value="dark">Dark</option>
              <option value="light">Light</option>
            </select>
          </FormField>
        </div>
      </div>
    </div>
  );
}

function InviteStaffModal({ open, onClose, branchId, onDone }) {
  const [form,   setForm]   = useState({ name:"", email:"", role:"staff" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      await orgAdmin.createBranchStaff({ ...form, facility: branchId });
      toast.success(`${form.name} invited.`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to invite."); }
    finally { setSaving(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Invite Staff Member" width="420px">
      <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>
        <FormField label="Full Name" required><input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} /></FormField>
        <FormField label="Email" required><input style={inputStyle} type="email" value={form.email} onChange={e => set("email", e.target.value)} /></FormField>
        <FormField label="Role">
          <select style={selectStyle} value={form.role} onChange={e => set("role", e.target.value)}>
            <option value="staff">Staff</option>
            <option value="facility_admin">Facility Admin</option>
          </select>
        </FormField>
        <div style={{ display:"flex", gap:"10px", justifyContent:"flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={saving || !form.name.trim() || !form.email.trim()}>{saving ? "Inviting…" : "Send Invite"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  root:         { padding:"32px", fontFamily:"'DM Sans','Segoe UI',sans-serif", maxWidth:"860px" },
  breadcrumb:   { display:"flex", alignItems:"center", gap:"6px", fontSize:"13px", marginBottom:"16px" },
  breadcrumbLink:{ background:"none", border:"none", color:"#8CBCE0", cursor:"pointer", padding:0, fontSize:"13px", fontFamily:"inherit", textDecoration:"underline" },
  tabs:         { display:"flex", gap:"4px", borderBottom:"1px solid var(--surface-border)", marginBottom:"24px" },
  tab:          { background:"none", border:"none", borderBottom:"2px solid transparent", padding:"10px 16px", fontSize:"13px", fontWeight:"500", color:"var(--text-secondary)", cursor:"pointer", fontFamily:"inherit", marginBottom:"-1px" },
  tabActive:    { color:"#8CBCE0", borderBottomColor:"#408DC9" },
  panel:        {},
  infoCard:     { background:"var(--surface-bg)", border:"1px solid var(--surface-border)", borderRadius:"12px", overflow:"hidden" },
  infoCardTitle:{ padding:"12px 18px", fontSize:"11px", fontWeight:"700", color:"#408DC9", letterSpacing:"0.08em", textTransform:"uppercase", borderBottom:"1px solid var(--surface-border)", background:"rgba(64,141,201,0.05)" },
  infoCardBody: { padding:"16px 18px", display:"flex", flexDirection:"column", gap:"10px" },
  infoRow:      { display:"flex", justifyContent:"space-between", alignItems:"center" },
  infoKey:      { fontSize:"13px", color:"var(--text-secondary)" },
  infoVal:      { fontSize:"13px", color:"var(--text-primary)", fontWeight:"500" },
  staffRow:     { display:"flex", alignItems:"center", gap:"12px", padding:"12px 16px", background:"var(--surface-bg)", border:"1px solid var(--surface-border)", borderRadius:"10px" },
  staffAvatar:  { width:"36px", height:"36px", borderRadius:"8px", background:"linear-gradient(135deg,#408DC9,#7C3AED)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"14px", fontWeight:"700", color:"#fff", flexShrink:0 },
};
